// ABOUTME: Cliente de la función generateTryOn con el protocolo de las callable functions
// ABOUTME: (POST {data} → {result} | {error}), sin cargar el SDK de Firebase.
export interface TryOnResult {
  imageBase64: string;
  mimeType: string;
  productUrl: string;
  remainingToday: number;
}

export class TryOnError extends Error {
  constructor(message: string, readonly code: string) {
    super(message);
  }
}

export function deviceId(): string {
  const key = "dreadlove.device";
  try {
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const id = crypto.randomUUID();
    localStorage.setItem(key, id);
    return id;
  } catch {
    return crypto.randomUUID();
  }
}

export async function generateTryOn(
  baseUrl: string,
  styleId: string,
  hairstyleId: string,
  imageBase64: string,
  beautify = 0,
  aspectRatio = "3:4",
  padded = false,
): Promise<TryOnResult> {
  const res = await fetch(`${baseUrl.replace(/\/$/, "")}/generateTryOn`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ data: { deviceId: deviceId(), styleId, hairstyleId, imageBase64, mimeType: "image/jpeg", beautify, aspectRatio, padded } }),
  });
  const body = (await res.json()) as { result?: TryOnResult; error?: { message: string; status: string } };
  if (!res.ok || body.error) {
    throw new TryOnError(body.error?.message ?? `Error ${res.status}`, body.error?.status ?? "UNKNOWN");
  }
  return body.result!;
}

export interface ViewResult {
  view: "right" | "left" | "back";
  imageBase64: string;
  mimeType: string;
}

export async function generateViews(baseUrl: string, imageBase64: string, aspectRatio: string): Promise<{ views: ViewResult[]; remainingToday: number }> {
  const res = await fetch(`${baseUrl.replace(/\/$/, "")}/generateViews`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ data: { deviceId: deviceId(), imageBase64, mimeType: "image/jpeg", aspectRatio } }),
  });
  const body = (await res.json()) as { result?: { views: ViewResult[]; remainingToday: number }; error?: { message: string; status: string } };
  if (!res.ok || body.error) throw new TryOnError(body.error?.message ?? `Error ${res.status}`, body.error?.status ?? "UNKNOWN");
  return body.result!;
}

async function call<T>(baseUrl: string, name: string, data: Record<string, unknown>): Promise<T> {
  const res = await fetch(`${baseUrl.replace(/\/$/, "")}/${name}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ data }),
  });
  const body = (await res.json()) as { result?: T; error?: { message: string; status: string } };
  if (!res.ok || body.error) throw new TryOnError(body.error?.message ?? `Error ${res.status}`, body.error?.status ?? "UNKNOWN");
  return body.result!;
}

export function startOrbit(
  baseUrl: string,
  imageBase64: string,
  aspectRatio: string,
  styleId: string,
  hairstyleId: string,
): Promise<{ operationName: string; remainingToday: number; durationSeconds: number }> {
  return call(baseUrl, "startOrbit", { deviceId: deviceId(), imageBase64, mimeType: "image/jpeg", aspectRatio, styleId, hairstyleId });
}

export function pollOrbit(baseUrl: string, operationName: string): Promise<{ done: boolean; videoBase64?: string; mimeType?: string }> {
  return call(baseUrl, "pollOrbit", { operationName });
}

export interface GenerationLog {
  /** Identificador corto de la foto, el mismo que va en los UTM de los enlaces de compra. */
  shotId: string;
  styleId: string;
  hairstyleId: string;
  beautify: number;
  padded: boolean;
  composed: boolean;
  aspect: string;
  ms: number;
  checks: { faceDetected: boolean; yawSelfie: number | null; yawResult: number | null; yawDrift: boolean };
}

export function logGeneration(baseUrl: string, data: GenerationLog): Promise<{ id: string }> {
  return call(baseUrl, "logGeneration", { deviceId: deviceId(), ...data });
}

export function rateGeneration(baseUrl: string, id: string, rating: 1 | -1, reason?: string): Promise<{ ok: boolean }> {
  return call(baseUrl, "rateGeneration", { id, rating, reason });
}
